import axios, { AxiosInstance, AxiosRequestConfig, AxiosStatic } from 'axios';
import { enqueueSnackbar } from 'notistack';
import { serverUrl } from 'app/constants/enviroments';

import logout from '../../helpers/auth/logout';
import { BaseSuccessResponse } from './types';

class HttpClient {
  private readonly api: AxiosInstance;

  private readonly config: AxiosRequestConfig = {
    baseURL: serverUrl,
    responseType: 'json',
    withCredentials: true,
    headers: {
      Accept: 'application/json',
      'Accept-Language': localStorage.getItem('i18nextLng') || 'az',
    },
  };

  constructor(axiosStatic: AxiosStatic) {
    this.api = axiosStatic.create(this.config);
    this.initInterceptors();
    this.initInterceptorsRequest();
  }

  public get<T, R = BaseSuccessResponse<T>>(url: string, config?: AxiosRequestConfig): Promise<R> {
    return this.api.get(url, config);
  }

  public delete<T, R = BaseSuccessResponse<T>>(
    url: string,
    config?: AxiosRequestConfig
  ): Promise<R> {
    return this.api.delete(url, config);
  }

  public post<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    return this.api.post(url, data, config);
  }

  public put<T, D, R = BaseSuccessResponse<T>>(
    url: string,
    data?: D,
    config?: AxiosRequestConfig
  ): Promise<R> {
    return this.api.put(url, data, config);
  }

  public patch<T, D, R = BaseSuccessResponse<T>>(
    url: string,
    data?: D,
    config?: AxiosRequestConfig
  ): Promise<R> {
    return this.api.patch(url, data, config);
  }

  public setLanguageToHeader(language: string): void {
    this.api.defaults.headers.language = language;
  }

  private initInterceptors(): void {
    this.api.interceptors.response.use(response => {
      return response.data;
    });
  }

  private initInterceptorsRequest(): void {
    this.api.interceptors.request.use(
      config => {
        config.headers.language = window?.location?.pathname?.split('/')[1] || 'az';

        return config;
      },
      error => Promise.reject(error)
    );

    this.api.interceptors.response.use(
      response => {
        // httpClient.api.defaults.params.business_key = response?.data?.business_key;
        return response;
      },
      async error => {
        const lang = localStorage.getItem('i18nextLng') || 'az';
        if (error?.response?.data?.error?.id === 'no_priv') {
          window.location.href = `/${lang}/not-has-access?previous_url=${window.location.pathname?.split('/')?.slice(2)?.join('/')}`;

          return Promise.reject(error);
        }

        const originalRequest = error.config;

        const refreshToken = sessionStorage.getItem('refreshToken');
        if (error?.response?.status === 401) {
          window.location.href = `/${lang}/login`;
          sessionStorage.clear();
        }
        if (error?.response?.status === 403 && refreshToken) {
          try {
            const result = await this.post<any>(
              `/api-web/v1/pub/refresh_token?refresh_token=${refreshToken}`
            );
            sessionStorage.setItem('refreshToken', result.refresh_token);
            // Retry the original request with the new token
            originalRequest.headers['Authorization'] = `Bearer ${result.access_token}`;

            return this.api.request(originalRequest);
          } catch (errRefresh: any) {
            if (errRefresh?.response?.status === 401) {
              window.location.href = `/${lang}/login`;
              sessionStorage.clear();
            }
          }
        }

        if (error?.response?.status === 403 && !refreshToken) {
          logout();
        }

        const message: React.ReactNode = (
          <div style={{ whiteSpace: 'pre-line' }}>{error?.response?.data?.error?.text}</div>
        );

        if (error?.response?.data?.error?.text) {
          enqueueSnackbar(message, {
            anchorOrigin: {
              vertical: 'top',
              horizontal: 'right',
            },
            variant: 'error',
          });
        }

        return Promise.reject(error);
      }
    );
  }
}

export const httpClient = new HttpClient(axios);
